import { useCallback, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { createEBTPaymentPlaceholder, updateEBTBalance } from 'api/jalapeno';
import useAccountQuery from 'api/queries/useAccountQuery';
import { QUERY_KEY as EBT_CHARGE_AMOUNT_QUERY_KEY } from 'api/queries/useEBTChargeAmountQuery';
import { SELECTIONS_QUERY_KEY } from 'api/queries/useSelectionsQuery';
import useSubscriptionQuery from 'api/queries/useSubscriptionQuery';
import useForage from 'contexts/ForageContext/useForage';
import useEBTBalanceCheck from 'shared/ebt/useEBTBalanceCheck';

const API_MESSAGES = {
    SUCCESS: 'Success',
    UNKNOWN_ERROR: 'Unknown server error',
};

/**
 * Custom hook for handling EBT payment capture flow
 *
 * Manages the complete EBT payment process including creating payment placeholders,
 * deferring payment capture, updating EBT balance, and handling loading/error states.
 *
 * @returns {Object} Hook return object
 * @returns {Function} returns.captureEBTPayment - Function to capture EBT payment
 * @returns {Function} returns.clearEBTPaymentCaptureError - Function to clear payment errors
 * @returns {boolean} returns.isCapturingEBTPayment - Loading state for payment capture
 * @returns {Error|null} returns.ebtPaymentCaptureError - Current payment capture error
 */
const useEBTPaymentCapture = () => {
    const queryClient = useQueryClient();

    const { data: account = {} } = useAccountQuery();
    const { data: subscription = {} } = useSubscriptionQuery();
    const { forage } = useForage();

    const { setBalanceCache } = useEBTBalanceCheck(subscription?.ebtCard?.last4);

    const [isCapturingEBTPayment, setIsCapturingEBTPayment] = useState(false);
    const [ebtPaymentCaptureError, setEbtPaymentCaptureError] = useState(null);

    const chargeID = account?.nextOrder?.chargeID;
    const paymentMethodRef = subscription?.ebtCard?.paymentMethodRef;

    /**
     * Captures EBT payment through a multi-step process
     *
     * @param {Object} params - Payment capture parameters
     * @param {number} params.ebtAmount - Amount to charge to EBT card
     * @param {Object} [params.pinElement] - PIN element for payment deferral
     * @returns {Promise<Object>} API response from EBT balance update
     * @throws {Error} When required parameters are missing or API calls fail
     */
    const captureEBTPayment = useCallback(
        async ({ ebtAmount, pinElement }) => {
            const missing = [
                !chargeID && 'chargeID',
                !paymentMethodRef && 'paymentMethodRef',
                !forage && 'forage',
                (ebtAmount == null || Number.isNaN(ebtAmount)) && 'ebtAmount',
            ].filter(Boolean);

            if (missing.length > 0) {
                throw new Error(`Missing: ${missing.join(', ')}`);
            }

            setIsCapturingEBTPayment(true);
            setEbtPaymentCaptureError(null);

            try {
                // Step 1: Create payment placeholder
                const paymentPlaceholderResponse = await createEBTPaymentPlaceholder(chargeID, {
                    paymentMethodRef,
                });

                if (paymentPlaceholderResponse?.msg !== API_MESSAGES.SUCCESS) {
                    throw new Error(paymentPlaceholderResponse?.msg || API_MESSAGES.UNKNOWN_ERROR);
                }

                // Step 2: Defer payment capture
                if (pinElement) {
                    await forage.deferPaymentCapture(pinElement, paymentPlaceholderResponse?.data?.paymentRef);
                }

                // Step 3: Update EBT balance
                const updateEBTBalanceResponse = await updateEBTBalance(chargeID, {
                    ebtAmount: Number(ebtAmount),
                    paymentRef: paymentPlaceholderResponse?.data?.paymentRef,
                });

                if (updateEBTBalanceResponse?.msg !== API_MESSAGES.SUCCESS) {
                    const balanceError = new Error(updateEBTBalanceResponse?.msg);
                    Object.assign(balanceError, updateEBTBalanceResponse?.data);
                    throw balanceError;
                }

                // Step 4: Update balance cache with latest returned from API
                setBalanceCache({
                    snap: updateEBTBalanceResponse?.data?.availableSNAPBalance,
                    updated: updateEBTBalanceResponse?.data?.updated,
                });

                // Step 5: Invalidate EBT charge amount query
                await Promise.all([
                    queryClient.invalidateQueries({
                        queryKey: [EBT_CHARGE_AMOUNT_QUERY_KEY],
                    }),
                    queryClient.invalidateQueries({
                        queryKey: [SELECTIONS_QUERY_KEY, chargeID],
                    }),
                ]);

                return { ...updateEBTBalanceResponse };
            } catch (err) {
                setEbtPaymentCaptureError(err);
                throw err;
            } finally {
                setIsCapturingEBTPayment(false);
            }
        },
        [chargeID, paymentMethodRef, forage]
    );

    /**
     * Clears any existing EBT payment capture errors
     */
    const clearEBTPaymentCaptureError = useCallback(() => {
        setEbtPaymentCaptureError(null);
    }, []);

    return {
        captureEBTPayment,
        clearEBTPaymentCaptureError,
        isCapturingEBTPayment,
        ebtPaymentCaptureError,
    };
};

export default useEBTPaymentCapture;
